import Icon from "@react-native-vector-icons/material-design-icons/static";
import React from "react";
import { Animated } from "react-native";
import { useTheme } from "react-native-paper";
import { ActivityIndicator } from "react-native-paper";
import type { ActivityIndicatorProps } from "react-native-paper";
import Svg from "react-native-svg";
import type { IconType } from "../../../icon";
import { useAppTheme } from "../../../useAppTheme";
import { AnimatedSvgCircle } from "../../AnimatedSvgCircle";
import Box from "../../Box";

type BaseCircularProgressBarProps = {
	size?: "small" | "medium" | "mediumAlt" | "large";
	strokeWidth?: number;
	trackColor?: string;
	progressColor?: string;
	icon?: IconType;
};

type CircularProgressBarProps = BaseCircularProgressBarProps & {
	progress: number;
};

const DETERMINATE_ANIMATION_DURATION = 1000;

const SIZE_MAP = {
	small: 24,
	medium: 40,
	mediumAlt: 48,
	large: 80,
} as const;

const ROTATION_MAP = {
	small: { background: -257, foreground: -77 },
	medium: { background: -260, foreground: -80 },
	mediumAlt: { background: -258, foreground: -78 },
	large: { background: -260, foreground: -80 },
} as const;

const STROKE_WIDTH_MAP = {
	small: 3,
	medium: 4,
	mediumAlt: 6,
	large: 8,
} as const;

const STROKE_DASH_OFFSET_MAP = {
	small: 6,
	medium: 7,
	mediumAlt: 10,
	large: 12,
} as const;

const BaseCircularProgressBar = ({
	progress,
	size = "small",
	strokeWidth = STROKE_WIDTH_MAP[size],
	trackColor,
	progressColor,
	icon,
}: CircularProgressBarProps) => {
	const { colors } = useTheme();
	const { current: timer } = React.useRef<Animated.Value>(
		new Animated.Value(0),
	);

	const sizeValue = SIZE_MAP[size];
	const radius = (sizeValue - strokeWidth) / 2;
	const circumference = radius * 2 * Math.PI;

	const finalTrackColor = trackColor || colors.secondaryContainer;
	const finalProgressColor = progressColor || colors.primary;

	React.useEffect(() => {
		Animated.timing(timer, {
			toValue: progress,
			duration: DETERMINATE_ANIMATION_DURATION,
			useNativeDriver: true,
		}).start();
	}, [progress]);

	const strokeDashoffset = timer.interpolate({
		inputRange: [0, 1],
		outputRange: [
			circumference,
			progress === 1 ? 0 : circumference / 18 + STROKE_DASH_OFFSET_MAP[size],
		],
	});
	const strokeDashoffset2 = timer.interpolate({
		inputRange: [0, 1],
		outputRange: [
			progress === 0 ? 0 : circumference / 18 + STROKE_DASH_OFFSET_MAP[size],
			circumference,
		],
	});

	const rotations = ROTATION_MAP[size];

	return (
		<Box position="relative" width={sizeValue} height={sizeValue}>
			<Svg width={sizeValue} height={sizeValue}>
				<AnimatedSvgCircle
					cx={sizeValue / 2}
					cy={sizeValue / 2}
					r={radius}
					stroke={finalTrackColor}
					strokeWidth={strokeWidth}
					strokeDasharray={circumference}
					strokeDashoffset={strokeDashoffset2}
					fill="none"
					strokeLinecap="round"
					transform={`translate(0,${sizeValue}) scale(1,-1) rotate(${rotations.background}, ${sizeValue / 2}, ${sizeValue / 2})`}
				/>
				<AnimatedSvgCircle
					cx={sizeValue / 2}
					cy={sizeValue / 2}
					r={radius}
					stroke={finalProgressColor}
					strokeWidth={strokeWidth}
					strokeDasharray={circumference}
					strokeDashoffset={strokeDashoffset}
					strokeLinecap="round"
					fill="none"
					transform={`rotate(${rotations.foreground}, ${sizeValue / 2}, ${sizeValue / 2})`}
				/>
			</Svg>
			{icon && (
				<Box
					position="absolute"
					top={0}
					left={0}
					right={0}
					bottom={0}
					justifyContent="center"
					alignItems="center"
				>
					<Icon name={icon} size={21} />
				</Box>
			)}
		</Box>
	);
};

const CircularProgressBarIndeterminate = ({
	...activityIndicatorProps
}: ActivityIndicatorProps) => {
	const theme = useAppTheme();

	return <ActivityIndicator theme={theme} {...activityIndicatorProps} />;
};

export const CircularProgressBar = Object.assign(BaseCircularProgressBar, {
	Indeterminate: CircularProgressBarIndeterminate,
}) as React.FC<CircularProgressBarProps> & {
	Indeterminate: React.FC<ActivityIndicatorProps>;
};
